import Client from "./client";

/**
 * The signed-in account's own settings - what the Account card on a
 * project's home page changes (see components/Account).
 *
 * Every one of these resolves to the updated user in the same shape
 * /auth/whoami returns, so the caller's only job on success is to put it
 * back in the store (keeping the access token it already holds - none of
 * these change it).
 */

const authHeaders = (accessToken) => (accessToken ? {Authorization: `Bearer ${accessToken}`} : {});

const failed = (e, fallback) => e.response?.data || {error: fallback};

export const ChangeAccountName = ({name, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.patch('/api/account/name', {name}, {headers: authHeaders(accessToken)}).then((res) => {
            resolve(res.data.user);
        }).catch((e) => {
            reject(failed(e, "Something went wrong saving your name. Please try again."));
        })
    })
}

// Step one of two. Nothing about the account changes here: the new address
// is parked and a 5-digit code goes to it, which is what proves that
// mailbox works before the account starts depending on it (see
// api/lib/emailChange.js). Resolves with `sentTo` - the address the code
// went to - plus the user, whose `pendingEmail` now says a change is
// waiting.
export const StartEmailChange = ({email, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/account/email', {email}, {headers: authHeaders(accessToken)}).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(failed(e, "Something went wrong changing your email. Please try again."));
        })
    })
}

// Step two: the code comes back and the pending address becomes the real
// one. The API throttles this per account (429 with `retryAfter` seconds),
// on the same window as asking for the code.
export const ConfirmEmailChange = ({code, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post('/api/account/email/confirm', {code}, {headers: authHeaders(accessToken)}).then((res) => {
            resolve(res.data.user);
        }).catch((e) => {
            reject(failed(e, "Something went wrong changing your email. Please try again."));
        })
    })
}

export const CancelEmailChange = ({accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete('/api/account/email', {headers: authHeaders(accessToken)}).then((res) => {
            resolve(res.data.user);
        }).catch((e) => {
            reject(failed(e, "Something went wrong. Please try again."));
        })
    })
}

/**
 * Uploads a new account icon.
 *
 * `crop` is the square the user positioned in the preview, in the source
 * image's own pixels: {left, top, size}. The original file is sent, not a
 * canvas of the crop - the API does the cutting and the resize to 64x64
 * itself (see api/lib/imageProcessing.js's processAvatar), so the stored
 * icon doesn't depend on what the browser's canvas did.
 */
export const UploadAccountIcon = ({file, crop, accessToken}) => {
    return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append("image", file);
        if (crop) {
            formData.append("cropLeft", Math.round(crop.left));
            formData.append("cropTop", Math.round(crop.top));
            formData.append("cropSize", Math.round(crop.size));
        }

        Client.post('/api/account/avatar', formData, {headers: authHeaders(accessToken)}).then((res) => {
            resolve(res.data.user);
        }).catch((e) => {
            reject(failed(e, "Something went wrong uploading your icon. Please try again."));
        })
    })
}

export const RemoveAccountIcon = ({accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete('/api/account/avatar', {headers: authHeaders(accessToken)}).then((res) => {
            resolve(res.data.user);
        }).catch((e) => {
            reject(failed(e, "Something went wrong removing your icon. Please try again."));
        })
    })
}

/**
 * Assignment email alerts.
 *
 * Stored per kind of work - `{bugs, supportTickets, tasks}` - even though
 * Manage Email Alerts offers a single switch today. `all` sets every kind at
 * once, which is what that switch sends; `alerts` names individual kinds and
 * leaves the rest alone, which is what per-kind checkboxes would send. Both
 * together is allowed and reads in that order: `all` is the baseline, named
 * kinds override it.
 */
export const UpdateEmailAlerts = ({all, alerts, accessToken}) => {
    return new Promise((resolve, reject) => {
        const payload = {};
        if (all !== undefined) payload.all = all;
        if (alerts !== undefined) payload.alerts = alerts;

        Client.patch('/api/account/alerts', payload, {headers: authHeaders(accessToken)}).then((res) => {
            resolve(res.data.user);
        }).catch((e) => {
            reject(failed(e, "Something went wrong saving your alert settings. Please try again."));
        })
    })
}
